import { Typography, SecondaryButton, PrimaryButton } from '@weareopenr/component-library';
import CollapsibleSection from 'src/components/CollapsibleSection';
import { IPublishFormValues, PublishType, Steps } from '../../types';
import { ProductSizeItem } from 'src/api/types';
import styles from './SummaryView.module.scss';
import { usePriceScheme } from 'src/hooks/usePriceScheme';
import { ModalTitle } from '../../../ModalTitle/ModalTitle';
import { useVenues } from '../../../../hooks/useVenues/useVenues';
import { useMemo } from 'react';
import { IntegrationType } from '../../../../constants';
import { getVenuesForIntegrationType } from '../../../../helpers/getVenuesForIntegrationType/getVenuesForIntegrationType';
import { ButtonContainer } from '../../../ButtonContainer/ButtonContainer';
import { getPublishTypeLabel } from 'src/helpers/getPublishTypeLabel/getPublishTypeLabel';
import { formatDateTime } from 'src/helpers';
import { createSectionNumberCounter } from '../../utilities/createSectionNumberCounter';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { getPosInstances } from 'src/helpers/channels/getPosInstances';

interface ISummaryViewProps {
  defaultValues: Omit<IPublishFormValues, 'excludedItems'> & {
    excludedItems?: Pick<ProductSizeItem, 'item_id' | 'name'>[];
  };
  onPublish: () => void;
  isLoading?: boolean;
  submitDataToParent?: (data: Partial<IPublishFormValues>, nextStep?: Steps) => void;
  publishTypeLabel?: { title: string; description: string };
}

export const SummaryView = ({
  defaultValues,
  submitDataToParent,
  onPublish,
  isLoading = false,
  publishTypeLabel,
}: ISummaryViewProps): React.ReactElement => {
  const { data: priceScheme } = usePriceScheme(defaultValues.priceSchemeId);
  const { data: allVenues } = useVenues();
  const { data: externalSystems } = useExternalSystems();

  const venuesMap = useMemo(() => {
    const venues = getVenuesForIntegrationType(allVenues, IntegrationType.POINT_OF_SALE_MANAGEMENT);
    return new Map(venues.map((venue) => [venue.venue_id, venue.name]));
  }, [allVenues]);

  const instancesMap = useMemo(
    () =>
      new Map(
        getPosInstances(externalSystems).map(({ extensionInstanceId, extensionInstanceName }) => [
          extensionInstanceId,
          extensionInstanceName,
        ]),
      ),
    [externalSystems],
  );

  const isStandalone = !submitDataToParent;
  const includePricing = defaultValues.type === PublishType.PRICING;
  const typeLabel = publishTypeLabel ?? getPublishTypeLabel(defaultValues.type);

  const numberOfVenues = defaultValues.venues?.length ?? 0;
  const numberOfExcludedItems = defaultValues.excludedItems?.length ?? 0;

  const sortedExcludedItems = useMemo(
    () => [...(defaultValues.excludedItems ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [defaultValues.excludedItems],
  );

  const getVenueName = (venueId: string): string => venuesMap.get(venueId) ?? venueId;

  const getInstanceName = (instanceId: string): string => instancesMap.get(instanceId) ?? instanceId;

  const getSectionNumber = createSectionNumberCounter();

  return (
    <>
      <ModalTitle title={`Summary for ${defaultValues.scheduleName || typeLabel.title}`} />
      <div className={styles['sections']}>
        <CollapsibleSection number={getSectionNumber()} title="Publish Type" subtitle={typeLabel.description} />
        {includePricing && (
          <CollapsibleSection number={getSectionNumber()} title="Price Scheme" subtitle={priceScheme?.name || ''} />
        )}
        {!!defaultValues.instanceId && (
          <CollapsibleSection
            number={getSectionNumber()}
            title="Point of Sale Instance"
            subtitle={getInstanceName(defaultValues.instanceId)}
          />
        )}
        {includePricing && (
          <CollapsibleSection
            number={getSectionNumber()}
            title="Publish Venues"
            subtitle={`${numberOfVenues} ${pluralize('venue', numberOfVenues)} selected`}
          >
            {numberOfVenues > 0 && (
              <div className={styles['list-container']}>
                {defaultValues.venues?.map((venueId) => (
                  <div key={venueId}>
                    <Typography>{getVenueName(venueId)}</Typography>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>
        )}
        <CollapsibleSection
          number={getSectionNumber()}
          title="Published date & time"
          subtitle={defaultValues.publicationDate ? formatDateTime(defaultValues.publicationDate) : 'Publish now'}
        />
        {!!defaultValues.excludedItems && (
          <CollapsibleSection
            number={getSectionNumber()}
            title="Excluded Products"
            subtitle={`${numberOfExcludedItems} ${pluralize('product', numberOfExcludedItems)} excluded`}
          >
            {numberOfExcludedItems > 0 && (
              <div className={styles['list-container']}>
                {sortedExcludedItems.map((product) => (
                  <div key={product.item_id}>
                    <Typography>{product.name}</Typography>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>
        )}
      </div>
      {isStandalone ? (
        <div className={styles['close-button']}>
          <PrimaryButton onClick={onPublish} size="full-width">
            Close Summary
          </PrimaryButton>
        </div>
      ) : (
        <ButtonContainer
          left={
            <SecondaryButton
              onClick={() => submitDataToParent({}, Steps.excludeProducts)}
              size="full-width"
              buttonStyle="outline"
            >
              Back
            </SecondaryButton>
          }
          right={
            <PrimaryButton onClick={onPublish} size="full-width" loading={isLoading}>
              Create Publish
            </PrimaryButton>
          }
        />
      )}
    </>
  );
};
